import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Paper,
  styled,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import TagsSelect from '@src/components/AdminHome/Common/TagsSelect';
import ContactTagsSelect from '@src/components/AdminHome/Common/TagsSelect/ContactTagsSelect';
import { AnyTag } from '@src/components/AdminHome/Tags/types';
import DatePickerInput, {
  DatePickerInputHandle,
} from '@src/components/CustomUI/DateRangePicker/DatePickerInput';
import { TimeRangePicker } from '@src/components/CustomUI/DateRangePicker/TimePickerInput';
import PaperOutlined from '@src/components/UI/PaperOutlined';
import { chatSearchActions, ChatsFilters } from '@src/reducers/chatSearch';
import { activeChatSideSelector, timeLimit } from '@src/selectors/chats';
import {
  activeSideFiltersSelector,
  botAccountsFilterSelector,
  channelsFilterSelector,
  departmentsNodesFilterSelector,
  tagsFilterSelector,
} from '@src/selectors/chatSearch';
import { ChatSideType, NotificationStatus } from '@src/types/chats';
import { FilterTag } from '@src/types/chatsSearch';
import endOfToday from 'date-fns/endOfToday';
import { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

type AdvancedSearchFormProps = {
  isFiltersPopoverOpen?: boolean;
};

const AdvancedSearchForm = ({
  isFiltersPopoverOpen = true,
}: AdvancedSearchFormProps) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const activeChatSide = useSelector(activeChatSideSelector);
  const isChatsOnHold = activeChatSide === ChatSideType.ON_HOLD;
  const isChatsBot = activeChatSide === ChatSideType.BOT;

  const activeChatSideFilters = useSelector(
    activeSideFiltersSelector
  ) as ChatsFilters;

  const tagsFilter = useSelector(tagsFilterSelector);
  const channelsFilter = useSelector(channelsFilterSelector);
  const botAccountsFilter = useSelector(botAccountsFilterSelector);
  const departmentsFilter = useSelector(departmentsNodesFilterSelector);
  const { noResponseTimeLimit: minutes, status } = useSelector(timeLimit);

  const {
    conversationLimit = false,
    windowLimit = false,
    noAnswerFromAgent = false,
    dateFrom = undefined,
    dateTo = undefined,
    departmentNodes = [],
    channels = [],
    botAccounts = [],
    tags = [],
    contactTags = [],
    noAnswerFromAgentWithinTimeLimit = false,
    dateContactLastMsgFrom = undefined,
    dateContactLastMsgTo = undefined,
  } = activeChatSideFilters;

  const departmentNodesBotId = useMemo(
    () => departmentNodes.map(({ bot }) => bot.id) ?? [],
    [departmentNodes]
  );

  const channelsId = useMemo(
    () => channels.map(({ label }) => label) ?? [],
    [channels]
  );

  const botAccountsId = useMemo(
    () => botAccounts.map(({ id }) => id) ?? [],
    [botAccounts]
  );

  const onChangeFilters = (f: Partial<ChatsFilters>) =>
    dispatch(
      chatSearchActions.changeChatsFilters({
        filters: f,
        chatSide: activeChatSide,
      })
    );

  const cleanFilters = () =>
    dispatch(chatSearchActions.cleanChatsFilters(activeChatSide));

  const creationDatePickerRef = useRef<DatePickerInputHandle>(null);
  const lastMsgDatePickerRef = useRef<DatePickerInputHandle>(null);

  return (
    <AdvancedFormStyled>
      <Box p={2} width={280} component={Paper}>
        <Box
          mb={1}
          display="flex"
          flexDirection="column"
          alignItems="center"
          className="title"
        >
          {formatMessage({ id: 'Chats.AdvancedSearch.Title' })}
        </Box>
        <Divider />
        <Box my={1.5} display="flex" flexDirection="column">
          {isChatsOnHold && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={windowLimit}
                  onChange={({ target }) =>
                    onChangeFilters({ windowLimit: target.checked })
                  }
                  disabled={conversationLimit}
                />
              }
              label={formatMessage({
                id: 'Chats.AdvancedSearch.windowLimit',
              })}
            />
          )}

          {isChatsOnHold && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={noAnswerFromAgent}
                  onChange={({ target }) =>
                    onChangeFilters({ noAnswerFromAgent: target.checked })
                  }
                />
              }
              label={formatMessage({
                id: 'Chats.AdvancedSearch.noAnswerFromAgent',
              })}
            />
          )}
          {isChatsOnHold && status === NotificationStatus.ACTIVE && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={noAnswerFromAgentWithinTimeLimit}
                  onChange={({ target }) =>
                    onChangeFilters({
                      noAnswerFromAgentWithinTimeLimit: target.checked,
                    })
                  }
                />
              }
              label={formatMessage(
                { id: 'Chats.AdvancedSearch.noAnswerForXtime' },
                { min: minutes }
              )}
            />
          )}
        </Box>
        <Box
          onClick={() => {
            const picker = creationDatePickerRef.current;
            if (picker && !picker.isOpen()) {
              picker.focus();
              picker.open();
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <FormControl fullWidth>
            <InputLabel>
              {formatMessage({ id: 'Chats.AdvancedSearch.creationDate' })}
            </InputLabel>
            <DatePickerInput
              ref={creationDatePickerRef}
              label={formatMessage({ id: 'Chats.AdvancedSearch.creationDate' })}
              dateFormat="y-MM-dd"
              value={[dateFrom, dateTo]}
              selectsRange
              maxDate={endOfToday()}
              autoCloseRange
              parentPopoverOpen={isFiltersPopoverOpen}
              PopoverProps={{
                PaperProps: { component: CalendarStyled },
              }}
              onChange={value => {
                onChangeFilters({
                  dateFrom: value[0] ?? undefined,
                  dateTo: value[1] ?? undefined,
                });
              }}
              InputProps={{ endAdornment: null, fullWidth: true }}
              footer={
                <FormControl>
                  <TimeRangePicker
                    variant="column"
                    enforceEndAfterStart
                    value={[dateFrom, dateTo]}
                    onChange={value => {
                      onChangeFilters({
                        dateFrom: value[0] ?? undefined,
                        dateTo: value[1] ?? undefined,
                      });
                    }}
                  />
                  <Box px={1} pb={1}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      fullWidth
                      onClick={() =>
                        onChangeFilters({
                          dateFrom: undefined,
                          dateTo: undefined,
                        })
                      }
                    >
                      <FormattedMessage id="DateRangePicker.clear" />
                    </Button>
                  </Box>
                </FormControl>
              }
            />
          </FormControl>
        </Box>
        <Box mb={1.2} />
        {isChatsOnHold && (
          <Box
            onClick={() => {
              const picker = lastMsgDatePickerRef.current;
              if (picker && !picker.isOpen()) {
                picker.focus();
                picker.open();
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <FormControl fullWidth>
              <InputLabel>
                {formatMessage({
                  id: 'Chats.AdvancedSearch.lastMsgDate',
                })}
              </InputLabel>
              <DatePickerInput
                ref={lastMsgDatePickerRef}
                label={formatMessage({
                  id: 'Chats.AdvancedSearch.lastMsgDate',
                })}
                dateFormat="y-MM-dd"
                value={[dateContactLastMsgFrom, dateContactLastMsgTo]}
                selectsRange
                maxDate={endOfToday()}
                autoCloseRange
                parentPopoverOpen={isFiltersPopoverOpen}
                onChange={value => {
                  onChangeFilters({
                    dateContactLastMsgFrom: value[0] ?? undefined,
                    dateContactLastMsgTo: value[1] ?? undefined,
                  });
                }}
                PopoverProps={{
                  PaperProps: { component: CalendarStyled },
                }}
                InputProps={{ endAdornment: null, fullWidth: true }}
                footer={
                  <>
                    <TimeRangePicker
                      variant="column"
                      enforceEndAfterStart
                      value={[dateContactLastMsgFrom, dateContactLastMsgTo]}
                      onChange={value => {
                        onChangeFilters({
                          dateContactLastMsgFrom: value[0] ?? undefined,
                          dateContactLastMsgTo: value[1] ?? undefined,
                        });
                      }}
                    />
                    <Box px={1} pb={1}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        fullWidth
                        onClick={() =>
                          onChangeFilters({
                            dateContactLastMsgFrom: undefined,
                            dateContactLastMsgTo: undefined,
                          })
                        }
                      >
                        <FormattedMessage id="DateRangePicker.clear" />
                      </Button>
                    </Box>
                  </>
                }
              />
            </FormControl>
          </Box>
        )}
        <Box mb={1.2} />
        {!isChatsBot && (
          <Autocomplete
            multiple
            size="small"
            value={departmentNodes}
            options={departmentsFilter}
            getOptionLabel={department => department.name}
            renderTags={options => (
              <div className="inline-tags">
                {options.map(option => option.name).join(', ')}
              </div>
            )}
            getOptionSelected={option =>
              departmentNodesBotId.includes(option.bot.id)
            }
            onChange={(_, selectedOptions) => {
              onChangeFilters({
                departmentNodes: selectedOptions,
              });
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label={formatMessage({
                  id: 'Chats.AdvancedSearch.departmentsNodes',
                })}
              />
            )}
          />
        )}
        <Box mb={1.2} />
        <Autocomplete
          multiple
          size="small"
          value={channels}
          options={channelsFilter}
          getOptionLabel={option => option.label}
          renderTags={options => (
            <div className="inline-tags">
              {options.map(option => option.label).join(', ')}
            </div>
          )}
          getOptionSelected={option => channelsId.includes(option.label)}
          onChange={(_, selectedOptions) => {
            onChangeFilters({
              channels: selectedOptions,
            });
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={formatMessage({
                id: 'Chats.AdvancedSearch.channels',
              })}
            />
          )}
        />
        <Box mb={1.2} />
        <Autocomplete
          multiple
          size="small"
          value={botAccounts}
          options={botAccountsFilter}
          getOptionLabel={option =>
            option.provider.includes('WHATSAPP')
              ? option.identifier
              : option.alias
          }
          renderTags={options => (
            <div className="inline-tags">
              {options
                .map(option =>
                  option.provider.includes('WHATSAPP')
                    ? option.identifier
                    : option.alias
                )
                .join(', ')}
            </div>
          )}
          getOptionSelected={option => botAccountsId.includes(option.id)}
          onChange={(_, selectedOptions) => {
            onChangeFilters({
              botAccounts: selectedOptions,
            });
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              label={formatMessage({
                id: 'Chats.AdvancedSearch.botAccounts',
              })}
            />
          )}
        />
        <Box mb={1.2} />
        {isChatsOnHold && (
          <>
            {/* Note: dont have pagination services/tags/available-to-current-user */}
            <TagsSelect
              value={tags}
              options={tagsFilter}
              inputLabel={formatMessage({
                id: 'Chats.AdvancedSearch.tags',
              })}
              onChange={(_, items) => {
                onChangeFilters({ tags: items as FilterTag[] });
              }}
              getTagOption={({ color, emoji, tagName, botId }) => ({
                id: `${tagName}-${botId}`,
                label: `${emoji} ${tagName}`,
                color,
                emoji,
              })}
            />
            <Box mb={1.2} />
          </>
        )}
        <ContactTagsSelect
          value={contactTags}
          inputLabel={formatMessage({ id: 'All.ContactTags' })}
          onChange={cTags => {
            onChangeFilters({ contactTags: cTags as AnyTag[] });
          }}
          getTagOption={({ tagId, color, emoji, name }) => ({
            label: name,
            id: tagId as number,
            color,
            emoji,
          })}
        />
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="contained" color="primary" onClick={cleanFilters}>
            {formatMessage({
              id: 'Chats.AdvancedSearch.clear.button.label',
            })}
          </Button>
        </Box>
      </Box>
    </AdvancedFormStyled>
  );
};

const AdvancedFormStyled = styled('div')(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: '18px !important',
  },

  '& .title': {
    color: theme.palette.text.primary,
    paddingBottom: 5,
  },

  '& .MuiFormControl-root label': {
    fontSize: '0.96rem',
    color: theme.palette.text.secondary,
  },

  '& .MuiFormControlLabel-root .MuiTypography-root': {
    fontSize: '0.93rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
  },

  '& .tags-chip .MuiChip-deleteIcon': {
    color: 'rgba(0,0,0,0.54)',
  },
}));

export default AdvancedSearchForm;

const CalendarStyled = styled(PaperOutlined)({
  marginTop: 8,
  '& .react-datepicker__month-container': {
    marginInline: 'auto',
  },
  '& .react-datepicker__day, .react-datepicker__day:hover': {
    width: '2.2rem',
    height: '2.2rem',
  },
  '& .react-datepicker__day-name': {
    width: '2.2rem',
  },
});
